import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, MapPin, Instagram, Linkedin, Facebook, Youtube } from "lucide-react";
import { SITE, SOCIAL_LINKS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  instagram: Instagram,
  linkedin: Linkedin,
  facebook: Facebook,
  youtube: Youtube,
  x: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
  ),
};

export default function Footer() {
  const [socials, setSocials] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/site-config`).then(r => setSocials(r.data?.social_links || {})).catch(() => {});
  }, []);

  // Merge admin-managed URLs over the site.js defaults; only show items that have a URL.
  const visibleSocials = SOCIAL_LINKS
    .map((s) => ({ ...s, href: socials?.[s.icon] || (s.href !== "#" ? s.href : "") }))
    .filter((s) => s.href);
  return (
    <footer data-testid="site-footer" className="bg-[#0A0A0A] text-white relative">
      <div className="grain absolute inset-0 opacity-40 pointer-events-none"></div>
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
        {/* Top */}
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block mb-6">
              <img src="/logos/pratik-teknoloji-white.png" alt="Pratik Teknoloji" className="h-20 md:h-24 w-auto" />
            </Link>
            <p className="text-white/70 text-sm leading-relaxed max-w-sm">
              {SITE.fullName}. Yazılım, donanım, güvenlik ve teknik servis çözümlerini tek merkezden sunan yetkili teknoloji çözüm ortağınız.
            </p>
          </div>

          <div className="lg:col-span-2">
            <div className="text-white/50 text-[11px] tracking-[0.2em] uppercase font-bold mb-4">Kurumsal</div>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/hakkimizda" className="text-white/80 hover:text-[#FF3B30]">Hakkımızda</Link></li>
              <li><Link to="/neden-biz" className="text-white/80 hover:text-[#FF3B30]">Neden Biz</Link></li>
              <li><Link to="/is-ortaklari" className="text-white/80 hover:text-[#FF3B30]">İş Ortakları</Link></li>
              <li><Link to="/referanslar" className="text-white/80 hover:text-[#FF3B30]">Referanslar</Link></li>
              <li><Link to="/iletisim" className="text-white/80 hover:text-[#FF3B30]">İletişim</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="text-white/50 text-[11px] tracking-[0.2em] uppercase font-bold mb-4">Hizmetler</div>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/hizmetler/yazilim-cozumleri" className="text-white/80 hover:text-[#FF3B30]">Yazılım Çözümleri</Link></li>
              <li><Link to="/hizmetler/sunucu-kiralama" className="text-white/80 hover:text-[#FF3B30]">Sunucu Kiralama</Link></li>
              <li><Link to="/hizmetler/teknik-servis" className="text-white/80 hover:text-[#FF3B30]">Teknik Servis</Link></li>
              <li><Link to="/hizmetler/pos-sistemleri" className="text-white/80 hover:text-[#FF3B30]">POS Sistemleri</Link></li>
              <li><Link to="/hizmetler/barkod-sistemleri" className="text-white/80 hover:text-[#FF3B30]">Barkod Sistemleri</Link></li>
              <li><Link to="/hizmetler/guvenlik-sistemleri" className="text-white/80 hover:text-[#FF3B30]">Güvenlik Sistemleri</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="text-white/50 text-[11px] tracking-[0.2em] uppercase font-bold mb-4">İletişim</div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3"><Phone size={18} strokeWidth={1.8} className="mt-0.5 flex-none text-[#FF3B30]" /><a href={`tel:${SITE.phoneRaw}`} className="text-white/80 hover:text-white">{SITE.phone}</a></li>
              <li className="flex items-start gap-3"><Mail size={18} strokeWidth={1.8} className="mt-0.5 flex-none text-[#FF3B30]" /><a href={`mailto:${SITE.email}`} className="text-white/80 hover:text-white">{SITE.email}</a></li>
              <li className="flex items-start gap-3"><MapPin size={18} strokeWidth={1.8} className="mt-0.5 flex-none text-[#FF3B30]" /><span className="text-white/80">{SITE.address.line1}, {SITE.address.line2}<br/>{SITE.address.city} – {SITE.address.zip}</span></li>
            </ul>
            <div className="flex items-center gap-2 mt-5">
              {visibleSocials.map((s) => {
                const Icon = iconMap[s.icon] || Instagram;
                return (
                  <a
                    key={s.name}
                    href={s.href}
                    target="_blank" rel="noopener noreferrer"
                    data-testid={`social-${s.icon}`}
                    aria-label={s.name}
                    className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-[#FF3B30] transition-colors"
                  >
                    <Icon className="w-[18px] h-[18px]" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        <div className="h-px bg-white/10 mb-6"></div>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 text-xs text-white/50">
          <div>© 2026 {SITE.fullName}. Tüm hakları saklıdır.</div>
          <div className="flex flex-wrap gap-5">
            <Link to="/kvkk" className="hover:text-white">KVKK</Link>
            <Link to="/gizlilik" className="hover:text-white">Gizlilik</Link>
            <Link to="/cerez-politikasi" className="hover:text-white">Çerez Politikası</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
